import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAsset, getAssetDropdownList } from "../../ApiHelper/url_Helpers";
import CommonTable from "../../Components/CommonTable";
import { FaEye, FaLaptop, FaCheckCircle, FaClock, FaUndo, FaClipboardList, FaFileAlt, FaUser, FaHourglassHalf, FaRegCheckCircle } from "react-icons/fa";
import SortDropdown from "../../Components/SortDropDown";
import FilterDropdown from "../Approvals/ApprovalFilter";
import { Search } from "lucide-react";
import Loader from "../../Components/Loader";

const statusOptions = [
  { key: "", label: "All" },
  { key: "issued", label: "Issued" },
  { key: "assigned", label: "Assigned" },
  { key: "pending", label: "Pending" }
];

const sortAssetsOptions = [
  { key: "assignedDate", label: "Assigned Date" },
  { key: "requestDate", label: "Request Date" },
  { key: "id", label: "Asset ID" }
];

const MyAssets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialSearch = searchParams.get("search") || "";
  const initialStatus = searchParams.get("status") || "";
  const initialAssetTypeLabel = searchParams.get("asset_type") || "";
  const initialStartDate = searchParams.get("start_date") || "";
  const initialEndDate = searchParams.get("end_date") || "";

  const [page, setPage] = useState(initialPage);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearch);
  const [tempFromDate, setTempFromDate] = useState(initialStartDate);
  const [tempToDate, setTempToDate] = useState(initialEndDate);
  const [assetFilters, setAssetFilters] = useState({
    status: initialStatus,
    asset_type: "",
    asset_type_label: initialAssetTypeLabel,
    start_date: initialStartDate,
    end_date: initialEndDate,
  });
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAssets = async (currentPage = page) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: 10,
        search: debouncedSearchQuery,
        status: assetFilters.status,
        asset_type: assetFilters.asset_type,
        start_date: assetFilters.start_date,
        end_date: assetFilters.end_date,
      };

      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );

      const response = await getAsset(filteredParams);
      setAssets(response?.data || response?.data?.assets || response?.data || []);
      if (response?.data?.pagination) {
        setPaginationData(response.data.pagination);
      } else if (response?.data?.count !== undefined) {
        // Fallback for standard DRF pagination
        setPaginationData({
          current_page: currentPage,
          total_records: response.data.count,
          next: response.data.next,
          previous: response.data.previous
        });
      }
    } catch (error) {
      console.error("Failed to fetch assets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetList = async () => {
    setLoading(true);
    try {
      const assettypes = await getAssetDropdownList();
      const options = assettypes?.data?.asset_types || [];
      setAssetTypeOptions(options);
      
      if (initialAssetTypeLabel) {
        const match = options.find((type) => type.name === initialAssetTypeLabel);
        if (match) {
          setAssetFilters((prev) => {
            if (prev.asset_type === match.id) return prev;
            return { ...prev, asset_type: match.id };
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch assets:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset page to 1 when search changes, only if it actually changes and isn't initial load
      if (searchQuery !== initialSearch) {
        setPage(1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchAssetList();
  }, []);

  useEffect(() => {
    fetchAssets(page);
  }, [page, debouncedSearchQuery, assetFilters]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page.toString());
    if (debouncedSearchQuery) params.set("search", debouncedSearchQuery);
    if (assetFilters.status) params.set("status", assetFilters.status);
    if (assetFilters.asset_type_label) params.set("asset_type", assetFilters.asset_type_label);
    if (assetFilters.start_date) params.set("start_date", assetFilters.start_date);
    if (assetFilters.end_date) params.set("end_date", assetFilters.end_date);

    setSearchParams(params, { replace: true });
  }, [page, debouncedSearchQuery, assetFilters, setSearchParams]);

  const renderStatus = (status) => {
    return (
      <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md">
        {status}
      </span>
    );
  };

  const columns = [
    { key: "asset_id", label: "Asset ID" },
    {
      key: "asset_type",
      label: "Asset Name",
      render: (v, row) => (
        <div className="flex flex-col">
          <span className="text-md text-gray-500"> {row.asset_name}</span>
          <span className="font-medium text-sm">Type:  {row.asset_type}</span>
        </div>
      )
    },
    { key: "serial_number", label: "Serial Number" },
    { key: "issued_date", label: "Assigned Date" },
    { key: "assignment_status", label: "Status", render: renderStatus },
    // { key: "requestDate", label: "Request Date" }
  ];

  const rowActions = (row) => (
    <button
      onClick={() => navigate(`/company-assets/details/${row.id}`, { state: { asset_uuid: row?.asset_uuid } })}
      className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md text-gray-700 font-medium transition-colors"
    >
      <FaEye className="text-gray-500" />
      View
    </button>
  );

  return (
    <div className="px-4 md:p-8 bg-[#f8fafc] min-h-screen">
      {/* Header and Filters */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      ) : (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">My Assets</h2>
            <p className="text-gray-500 text-sm mt-1 mb-6">View all company assets assigned to you</p>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 text-blue-500 rounded-lg flex items-center justify-center text-md">
                  <FaLaptop />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Assets</p>
                  <h3 className="text-xl font-semibold">{assets?.summary?.currently_assigned || 0}</h3>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 text-blue-400 rounded-lg flex items-center justify-center text-md">
                  <FaClock />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">Needs My Action</p>
                  <h3 className="text-xl font-semibold">{assets?.summary?.needs_my_action || 0}</h3>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                <div className="w-12 h-12 bg-red-50 text-red-400 rounded-lg flex items-center justify-center text-md">
                  <FaUndo />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">Requests in Progress</p>
                  <h3 className="text-xl font-semibold">{assets?.summary?.requests_in_progress || 0}</h3>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/company-assets/requests')}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-50 text-cyan-500 rounded-lg flex items-center justify-center text-md">
                    <FaClipboardList />
                  </div>
                  <div>
                    <h3 className="text-md text-gray-500 font-medium">Asset Requests</h3>
                    {/* <h3 className="text-xl font-semibold">{assets?.summary?.requests_in_progress || 0}</h3> */}
                  </div>
                </div>
                <div className="text-cyan-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 justify-between">
              <div className="relative flex-grow sm:max-w-xs">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search asset ID...(AT-000)"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 md:gap-4 w-full sm:w-auto overflow-visible z-10">
                {/* <FilterDropdown 
                  // list={statusOptions}
                  // title="Status"
                  // keyName="status"
                  // name="Status By"
                  // value={assetFilters}
                  // onChange={(n) => setAssetFilters((s) => ({ ...s, ...n }))}
                  // onClear={() => setAssetFilters((s) => ({ ...s, status: "" }))}
                  // />*/}
                <FilterDropdown
                  list={assetTypeOptions.map((type) => ({ key: type.id, label: type.name }))}
                  title="Asset Type"
                  keyName="asset_type"
                  name="Asset Type"
                  value={assetFilters}
                  onChange={(n) => {
                    const match = assetTypeOptions.find(type => type.id === n.asset_type);
                    setAssetFilters((s) => ({ ...s, asset_type: n.asset_type, asset_type_label: match ? match.name : "" }));
                  }}
                  onClear={() => setAssetFilters((s) => ({ ...s, asset_type: "", asset_type_label: "" }))}
                />
                <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full sm:w-auto">
                  <input
                    type="date"
                    value={tempFromDate}
                    onChange={(e) => {
                      setTempFromDate(e.target.value);
                      if (!e.target.value) {
                        setTempToDate("");
                      }
                    }}
                    className="flex-1 sm:flex-none border border-gray-300 rounded-md p-2 px-4 text-sm font-semibold bg-white outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <input
                    type="date"
                    value={tempToDate}
                    onChange={(e) => setTempToDate(e.target.value)}
                    min={tempFromDate}
                    disabled={!tempFromDate}
                    className="flex-1 sm:flex-none border border-gray-300 rounded-md p-2 px-4 text-sm font-semibold bg-white outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    onClick={() => {
                      setAssetFilters((s) => ({
                        ...s,
                        start_date: tempFromDate,
                        end_date: tempToDate,
                      }));
                      setPage(1);
                    }}
                    className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-md disabled:opacity-50"
                    disabled={!tempFromDate || !tempToDate}
                  >
                    Go
                  </button>
                </div>
                <button
                  onClick={() => {
                    setAssetFilters({
                      status: "",
                      asset_type: "",
                      asset_type_label: "",
                      start_date: "",
                      end_date: "",
                    });
                    setTempFromDate("");
                    setTempToDate("");
                    setSearchQuery("");
                    setPage(1);
                  }}
                  className="px-4 py-2  border rounded-md text-sm text-gray-600 hover:bg-gray-50"
                >
                  Reset All
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
            <CommonTable
              data={Array.isArray(assets) ? assets : (assets?.assets || [])}
              rowKey="id"
              columns={columns}
              rowActions={rowActions}
              page={paginationData?.current_page || page}
              onPageChange={(url) => {
                if (url) {
                  try {
                    const urlObj = new URL(url);
                    const newPage = urlObj.searchParams.get("page");
                    setPage(newPage ? Number(newPage) : 1);
                  } catch (e) {
                    console.error("Invalid pagination URL:", url);
                  }
                }
              }}
              nextPageUrl={paginationData?.next}
              prevPageUrl={paginationData?.previous}
              totalCount={paginationData?.total_records || (Array.isArray(assets) ? assets.length : (assets?.assets?.length || 0))}
            />
          </div>

          {/* Timeline */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-8 mb-8">
            <h3 className="font-semibold text-gray-800 mb-8">Asset Transfer Process</h3>
            <div className="flex items-start justify-between relative px-2">
              {/* Connecting line */}
              <div className="absolute top-6 left-12 right-12 h-0.5 bg-gray-200 border-t border-dashed border-gray-300 z-0"></div>

              {/* Steps */}
              <div className="flex flex-col items-center relative z-10">
                <div className="w-12 h-12 rounded-full bg-cyan-100 text-cyan-500 flex items-center justify-center mb-3">
                  <FaFileAlt className="text-xl" />
                </div>
                <p className="text-sm font-semibold text-gray-800 text-center">1. Request Raised</p>
                <p className="text-xs text-gray-500 text-center">By Current Owner</p>
              </div>

              <div className="flex flex-col items-center relative z-10 w-1/5">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center mb-3">
                  <FaUser className="text-xl" />
                </div>
                <p className="text-sm font-semibold text-gray-800 text-center">2. HR Approval</p>
                <p className="text-xs text-gray-500 text-center">Reviewed By HR</p>
              </div>

              <div className="flex flex-col items-center relative z-10 w-1/5">
                <div className="w-12 h-12 rounded-full bg-orange-100 text-orange-500 flex items-center justify-center mb-3">
                  <FaHourglassHalf className="text-xl" />
                </div>
                <p className="text-sm font-semibold text-gray-800 text-center">3. Receiver Confirmation</p>
                <p className="text-xs text-gray-500 text-center">Pending receiver action</p>
              </div>

              <div className="flex flex-col items-center relative z-10 w-1/5">
                <div className="w-12 h-12 rounded-full bg-cyan-100 text-cyan-500 flex items-center justify-center mb-3">
                  <FaClipboardList className="text-xl" />
                </div>
                <p className="text-sm font-semibold text-gray-800 text-center">4. HR Verification</p>
                <p className="text-xs text-gray-500 text-center">Asset verified by HR</p>
              </div>

              <div className="flex flex-col items-center relative z-10 ">
                <div className="w-12 h-12 rounded-full bg-green-100 text-green-500 flex items-center justify-center mb-3">
                  <FaRegCheckCircle className="text-xl" />
                </div>
                <p className="text-sm font-semibold text-gray-800 text-center">5. Completed</p>
                <p className="text-xs text-gray-500 text-center">Ownership Updated</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAssets;
